export function normalizeGstin(gstNo) {
    return String(gstNo || '').trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
}

export function isValidBuyerGstin(gstNo) {
    const gst = normalizeGstin(gstNo);
    if (!gst || ['URP', 'NA', 'N/A', 'NIL', 'NONE'].includes(gst)) {
        return false;
    }
    if (gst.length !== 15) {
        return false;
    }
    return /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(gst);
}

export function resolveInvoiceTypeMode({ config, billingGstNo, einvoiceIrn }) {
    if (einvoiceIrn) {
        return 'B2B';
    }
    if (isValidBuyerGstin(billingGstNo)) {
        return 'B2B';
    }
    if (config?.invoice_type) {
        return config.invoice_type;
    }
    return 'B2C';
}

export function resolveInvoiceUpdateMode(params) {
    return resolveInvoiceTypeMode(params);
}

export function resolveInvoiceCreateMode({ config, billingGstNo }) {
    return resolveInvoiceTypeMode({ config, billingGstNo });
}

export const GST_DOCUMENT_MODES = {
    NONE: 'none',
    EINVOICE: 'einvoice',
    EINVOICE_EWAY: 'einvoice_eway',
};

export function resolveGstDocumentModeFromFlags(isEinvoiceApplicable, isEwayApplicable) {
    if (isEinvoiceApplicable && isEwayApplicable) {
        return GST_DOCUMENT_MODES.EINVOICE_EWAY;
    }
    if (isEinvoiceApplicable) {
        return GST_DOCUMENT_MODES.EINVOICE;
    }
    return GST_DOCUMENT_MODES.NONE;
}

export function flagsFromGstDocumentMode(mode) {
    const normalized = String(mode || GST_DOCUMENT_MODES.NONE).trim().toLowerCase();
    if (normalized === GST_DOCUMENT_MODES.EINVOICE_EWAY) {
        return { is_einvoice_applicable: true, is_eway_bill_applicable: true };
    }
    if (normalized === GST_DOCUMENT_MODES.EINVOICE) {
        return { is_einvoice_applicable: true, is_eway_bill_applicable: false };
    }
    return { is_einvoice_applicable: false, is_eway_bill_applicable: false };
}

export function getDefaultGstDocumentMode(config) {
    if (!config) {
        return GST_DOCUMENT_MODES.NONE;
    }
    if (config.selected_gst_document_mode) {
        return config.selected_gst_document_mode;
    }
    if (config.default_gst_document_mode) {
        return config.default_gst_document_mode;
    }
    return resolveGstDocumentModeFromFlags(
        config.is_einvoice_applicable,
        config.is_eway_bill_applicable,
    );
}

export function getAllowedGstDocumentModes(config) {
    if (Array.isArray(config?.allowed_gst_document_modes) && config.allowed_gst_document_modes.length) {
        return config.allowed_gst_document_modes;
    }
    const modes = [GST_DOCUMENT_MODES.NONE];
    if (config?.is_einvoice_applicable) {
        modes.push(GST_DOCUMENT_MODES.EINVOICE);
        if (config?.is_eway_bill_applicable) {
            modes.push(GST_DOCUMENT_MODES.EINVOICE_EWAY);
        }
    }
    return modes;
}

export function shouldGenerateEinvoiceOnSave(config, selectedMode) {
    const mode = selectedMode || getDefaultGstDocumentMode(config);
    return Boolean(
        config?.can_generate_einvoice
        && (mode === GST_DOCUMENT_MODES.EINVOICE || mode === GST_DOCUMENT_MODES.EINVOICE_EWAY),
    );
}

export function shouldGenerateEwayOnSave(config, selectedMode) {
    const mode = selectedMode || getDefaultGstDocumentMode(config);
    return Boolean(
        config?.can_generate_einvoice
        && config?.is_eway_bill_applicable
        && mode === GST_DOCUMENT_MODES.EINVOICE_EWAY,
    );
}

export function isB2CWithoutEinvoiceSave(config) {
    return Boolean(config?.is_einvoice_applicable && !config?.can_generate_einvoice);
}

export function getInvoiceSaveConfirmOptions(config, { isUpdate = false, selectedMode } = {}) {
    if (isB2CWithoutEinvoiceSave(config)) {
        return {
            title: isUpdate ? 'Update as B2C Invoice?' : 'Save as B2C Invoice?',
            html: `<p>Company e-Invoice is enabled, but buyer GSTIN is not available.</p>
                <p>Do you want to ${isUpdate ? 'update' : 'save'} this invoice as <b>B2C</b> without e-Invoice?</p>
                <p style="margin-top:8px;color:#856404;">Update customer GST in Customer Master later, then generate e-Invoice from the invoice.</p>`,
            icon: 'question',
            confirmButtonText: isUpdate ? 'Update as B2C' : 'Save as B2C',
            denyButtonText: isUpdate ? 'Update as B2C and Print' : 'Save as B2C and Print',
            cancelButtonText: 'Cancel',
        };
    }

    return {
        title: isUpdate ? 'Do you want to Update?' : 'Do you want to save?',
        html: getEinvoiceSaveMessage(config, selectedMode),
        confirmButtonText: isUpdate ? 'Update only' : 'Save only',
        denyButtonText: isUpdate ? 'Update and Printout' : 'Save and Printout',
        cancelButtonText: 'No',
    };
}

export function getEinvoiceSaveMessage(config, selectedMode) {
    if (!config) {
        return '';
    }
    const mode = selectedMode || getDefaultGstDocumentMode(config);
    if (config.can_generate_einvoice && mode === GST_DOCUMENT_MODES.EINVOICE_EWAY) {
        return `<p style="margin-top:8px;color:#0b5ed7;">E-Invoice IRN + E-Way Bill will be generated via Adaequare GSP <b>${(config.einvoice_environment || 'sandbox').toUpperCase()}</b>.</p>`;
    }
    if (config.can_generate_einvoice && mode === GST_DOCUMENT_MODES.EINVOICE) {
        return `<p style="margin-top:8px;color:#0b5ed7;">E-Invoice IRN will be generated via Adaequare GSP <b>${(config.einvoice_environment || 'sandbox').toUpperCase()}</b>.</p>`;
    }
    if (config.is_einvoice_applicable && !config.has_buyer_gstin) {
        return `<p style="margin-top:8px;color:#856404;">Buyer GSTIN is not available. Invoice will be saved as <b>B2C</b> without e-Invoice. Update customer GST later, then generate e-Invoice from the invoice.</p>`;
    }
    return '';
}
